/**
 * formation-templates.ts — v0M4.3.2 (2026-07-23).
 *
 * Typed loader for the formation templates emitted from BattleMap.xlsx
 * by `scripts/convert-xlsx.ts` into `src/data/generated/battle-map.json`.
 *
 * Each template is a curated 20×20 grid of tagged priority slots that
 * define where cohorts of a given type should deploy. Cells matching
 * the pattern `<TAG><PRIORITY>` (e.g. `M1`, `R7`, `A12`) are slots;
 * everything else is empty ground.
 *
 * Placement algorithm (v0M4.3.4):
 *   1. Sort cohorts by id ascending (creation order = deterministic).
 *   2. For each cohort, resolve its primary tag from CohortSpec.tags:
 *      - Melee → 'M'; Ranged → 'R'; Siege → 'S';
 *      - Cavalry stat cluster (moveSpeedCombat >= 2) → 'C';
 *      - Otherwise → 'A' (any).
 *   3. Fill the lowest-numbered same-tag slot that is currently free
 *      AND lands on a passable terrain cell.
 *   4. If all same-tag slots are exhausted / blocked, fall through to
 *      the lowest-numbered 'A' slot that is free + passable.
 *   5. If all 'A' slots are also exhausted, spiral-search for the
 *      nearest passable cell from the desired slot's coordinates.
 *
 * Mirror rule: attacker-side deployment uses the same template
 * reflected through the 20×20 grid centre (r' = 19 - r, c' = 19 - c).
 * `battle-factory.ts` handles the reflection at apply time.
 */

import { assertSchemaVersion, BATTLE_MAP_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/battle-map.json';

export type FormationSlotTag = 'A' | 'M' | 'R' | 'C' | 'S';

export interface FormationSlot {
  readonly tag: FormationSlotTag;
  /** Fill priority within the tag; M1 fills before M2 within Melee. */
  readonly priority: number;
  /** 0-indexed row (0 = top of 20×20 grid). */
  readonly row: number;
  /** 0-indexed column (0 = left of 20×20 grid). */
  readonly col: number;
}

export interface FormationTemplate {
  readonly name: string;
  /** All slots in this template. Iteration order isn't semantically
   *  meaningful — callers filter + sort by (tag, priority) as needed. */
  readonly slots: readonly FormationSlot[];
}

export type FormationTemplates = Readonly<Record<string, FormationTemplate>>;

/**
 * Load the formation-template dictionary. Asserts schema version.
 */
export function loadFormationTemplates(): FormationTemplates {
  assertSchemaVersion(rawBlob, BATTLE_MAP_SCHEMA_VERSION, 'src/data/generated/battle-map.json');
  const blob = rawBlob as unknown as { formationTemplates: FormationTemplates };
  return blob.formationTemplates;
}

/**
 * Return slots of a given tag in ascending priority order.
 */
export function slotsByTag(
  template: FormationTemplate,
  tag: FormationSlotTag,
): FormationSlot[] {
  const out: FormationSlot[] = [];
  for (const s of template.slots) {
    if (s.tag === tag) out.push(s);
  }
  out.sort((a, b) => a.priority - b.priority);
  return out;
}

/**
 * MFix6.6.1 sibling convention (kept simple for v0M4.3): a cohort's
 * "primary tag" for formation placement is the most specific of its
 * combat tags. Precedence when a cohort has multiple tags:
 *   Siege > Ranged > Cavalry > Melee > any
 *
 * "Cavalry" is a stat-cluster property (see §6.1 #21 tag statement of
 * record: cavalry-ness is a stat cluster, not a tag). This helper maps
 * a cohort spec to its formation slot tag; the caller decides which
 * heuristic identifies cavalry (typically moveSpeedCombat >= 2).
 */
export function cohortPrimaryTag(args: {
  tags: readonly string[];
  isCavalry: boolean;
}): FormationSlotTag {
  const { tags, isCavalry } = args;
  if (tags.includes('Siege')) return 'S';
  if (tags.includes('Ranged')) return 'R';
  if (isCavalry) return 'C';
  if (tags.includes('Melee')) return 'M';
  return 'A';
}
